import { useCallback } from 'react'
import { useClerk, useSession as useClerkSession } from '@clerk/nextjs'

export {
  SignedIn,
  ClerkProvider,
  SignInButton,
  UserButton,
  UserProfile,
  useClerk,
  useUser,
  SignIn,
  useAuth,
  useSignUp,
  useSignIn,
  AuthenticateWithRedirectCallback,
  SignOutButton,
} from '@clerk/nextjs'
export {
  isClerkAPIResponseError,
  isEmailLinkError,
  EmailLinkErrorCodeStatus,
} from '@clerk/nextjs/errors'

export function useSession() {
  const session = useClerkSession()
  const clerk = useClerk()

  const buildSignInUrl = (...args: Parameters<typeof clerk.buildSignInUrl>) =>
    clerk.buildSignInUrl(...args)

  return { ...session, buildSignInUrl }
}

export const DRIVE_SCOPE = 'https://www.googleapis.com/auth/drive.file'

/**
 * Starts connecting a Google account to the signed-in user's Clerk profile,
 * in-app, requesting the email/profile scopes so the connected account carries
 * an email. Returns the Google consent URL the caller should navigate to
 * (matching the `connectAccount`/`reauthorizeAccount` pattern), or null when
 * Clerk gives us no URL so the caller can surface a failure instead of hanging.
 */
export const useConnectGoogleAccount = () => {
  const { session, isSignedIn } = useClerkSession()

  const connect = useCallback(
    async (redirectUrl: string): Promise<string | null> => {
      if (!isSignedIn) {
        throw new Error('User is not signed in')
      }

      const res = await session.user.createExternalAccount({
        strategy: 'oauth_google',
        additionalScopes: ['email', 'profile'],
        redirectUrl,
      })

      return res.verification?.externalVerificationRedirectURL?.href ?? null
    },
    [session, isSignedIn]
  )

  return { connect }
}

export const useGoogleScope = () => {
  const { session, isSignedIn } = useClerkSession()

  const needsConnection = useCallback(() => {
    if (!isSignedIn) {
      return true
    }

    const googleAccount = session.user.externalAccounts.find(
      (account) => account.provider === 'google'
    )
    if (!googleAccount) {
      return true
    }

    return false
  }, [session, isSignedIn])

  const needsAuthorization = useCallback(
    (scope?: string) => {
      if (!isSignedIn) {
        return true
      }

      if (needsConnection()) {
        return true
      }

      const googleAccount = session.user.externalAccounts.find(
        (account) => account.provider === 'google'
      )
      if (!googleAccount) {
        return true
      }

      if (
        googleAccount.verification?.expireAt &&
        googleAccount.verification.expireAt < new Date()
      ) {
        return true
      }

      return Boolean(scope && !googleAccount.approvedScopes.includes(scope))
    },
    [session, needsConnection, isSignedIn]
  )

  const connectAccount = async ({
    scope,
    redirectUrl,
    onSuccess,
  }: {
    scope: string
    redirectUrl: string
    onSuccess?: (verificationUrl: string) => void
  }) => {
    if (!isSignedIn) {
      throw new Error('User is not signed in')
    }

    if (needsConnection()) {
      const res = await session.user.createExternalAccount({
        strategy: 'oauth_google',
        additionalScopes: [scope],
        redirectUrl,
      })

      if (res.verification?.externalVerificationRedirectURL?.href) {
        onSuccess?.(res.verification.externalVerificationRedirectURL.href)
      }
    }
  }

  const reauthorizeAccount = async ({
    scope,
    redirectUrl,
    onSuccess,
  }: {
    scope: string
    redirectUrl: string
    onSuccess?: (verificationUrl: string) => void
  }) => {
    if (!isSignedIn) {
      throw new Error('User is not signed in')
    }

    if (needsAuthorization(scope)) {
      const googleAccount = session.user.externalAccounts.find(
        (account) => account.provider === 'google'
      )
      if (googleAccount) {
        const res = await googleAccount.reauthorize({
          additionalScopes: [scope],
          redirectUrl,
        })

        if (res.verification?.externalVerificationRedirectURL?.href) {
          onSuccess?.(res.verification.externalVerificationRedirectURL.href)
        }
      }
    }
  }

  return {
    needsConnection,
    needsAuthorization,
    connectAccount,
    reauthorizeAccount,
  }
}
